import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, DatePicker, Table, Tag, notification } from "antd";
import {
  clockIn,
  clockOut,
  getSessions,
  summarizeByDay,
  fmtHMS,
} from "../../../../service/trainerTime";
import { useAuth } from "../../../../provider/Auth.provider";

const { RangePicker } = DatePicker;

export default function TrainerTimesheet() {
  const { user } = useAuth();
  const trainerId = user?.uid;
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null); // active session
  const [sessions, setSessions] = useState([]);
  const [range, setRange] = useState(() => {
    const now = new Date();
    const start = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      0,
      0,
      0
    );
    return [start.getTime(), now.getTime()];
  });
  const [ticker, setTicker] = useState(0);
  const tickRef = useRef();

  // poll 1s to re-render elapsed for active session
  useEffect(() => {
    tickRef.current = setInterval(() => setTicker((t) => t + 1), 1000);
    return () => clearInterval(tickRef.current);
  }, []);

  const fetchSessions = async () => {
    if (!trainerId) return;
    setLoading(true);
    try {
      const list = await getSessions({
        trainerId,
        fromMs: range[0],
        toMs: range[1],
      });
      setSessions(list);
      setActive(list.find((s) => s.status === "active") || null);
    } catch (e) {
      console.error(e);
      notification.error({ message: "Failed to load sessions" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trainerId, range[0], range[1]]);

  const onClockIn = async () => {
    try {
      const res = await clockIn({ trainerId, context: {} });
      const newSession = { id: res.id, ...res.data };
      setSessions((prev) => [newSession, ...prev]); // add to top of list
      setActive(newSession);
      notification.success({ message: "Clocked in" });
    } catch (e) {
      console.error(e);
      notification.error({ message: "Clock-in failed" });
    }
  };

  const onClockOut = async () => {
    try {
      await clockOut({ trainerId, sessionId: active?.id });
      setActive(null);
      notification.success({ message: "Clocked out" });
      fetchSessions();
    } catch (e) {
      console.error(e);
      notification.error({ message: "Clock-out failed" });
    }
  };

  const todaySummary = useMemo(() => {
    const daily = summarizeByDay(sessions);
    const today = daily[0]; // most recent date first
    return today ? fmtHMS(today.totalMs) : "00:00:00";
  }, [sessions, ticker]);

  const columns = [
    {
      title: "Start",
      dataIndex: "startedAtMs",
      render: (ms) => (ms ? new Date(ms).toLocaleString() : "-"),
    },
    {
      title: "End",
      dataIndex: "endedAtMs",
      render: (_, r) =>
        r.status === "active" ? (
          <Tag color="gold">Active</Tag>
        ) : (
          new Date(r.endedAtMs).toLocaleString()
        ),
    },
    {
      title: "Duration",
      dataIndex: "durationMs",
      render: (_, r) =>
        r.status === "active" ? (
          <strong>{fmtHMS(Date.now() - (r.startedAtMs || Date.now()))}</strong>
        ) : (
          fmtHMS(r.durationMs || 0)
        ),
    },
    {
      title: "Context",
      dataIndex: "context",
      render: (c) =>
        c && (c.packageId || c.batchId || c.courseId || c.moduleId) ? (
          <div className="text-xs">
            {c.packageId ? <div>Pkg: {c.packageId}</div> : null}
            {c.batchId ? <div>Batch: {c.batchId}</div> : null}
            {c.courseId ? <div>Course: {c.courseId}</div> : null}
            {c.moduleId ? <div>Module: {c.moduleId}</div> : null}
          </div>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        ),
    },
    {
      title: "Source",
      dataIndex: "source",
      render: (s) => <Tag>{s || "manual"}</Tag>,
    },
  ];

  const onRange = (val) => {
    if (!val) return;
    const from = val[0].toDate().getTime();
    const to = val[1].toDate().getTime();
    setRange([from, to]);
  };

  return (
    <div className="p-4 text-white">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <div className="text-lg font-semibold">Timesheet</div>
          <div className="text-sm text-gray-400">Today: {todaySummary}</div>
        </div>
        <div className="flex items-center gap-2">
          <RangePicker showTime onChange={onRange} />
          {active ? (
            <Button type="primary" danger onClick={onClockOut}>
              Time Out
            </Button>
          ) : (
            <Button type="primary" onClick={onClockIn}>
              Time In
            </Button>
          )}
        </div>
      </div>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={sessions}
        pagination={{ pageSize: 10 }}
      />
    </div>
  );
}
